import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';

const getTodayInIsrael = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });
const formatDateValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getCurrentMonthRange = () => {
  const today = getTodayInIsrael();
  return {
    start_date: `${today.slice(0, 7)}-01`,
    end_date: today,
  };
};

const getDateFromValue = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getPresetRange = (preset) => {
  const todayValue = getTodayInIsrael();
  const today = getDateFromValue(todayValue);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (preset === 'today') {
    return { start_date: todayValue, end_date: todayValue };
  }

  if (preset === 'yesterday') {
    const dateValue = formatDateValue(yesterday);
    return { start_date: dateValue, end_date: dateValue };
  }

  if (preset === 'this_week') {
    const start = new Date(today);
    start.setDate(start.getDate() - start.getDay());
    return { start_date: formatDateValue(start), end_date: todayValue };
  }

  if (preset === 'last_week') {
    const end = new Date(today);
    end.setDate(end.getDate() - end.getDay() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return { start_date: formatDateValue(start), end_date: formatDateValue(end) };
  }

  if (preset === 'last_month') {
    const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = today.getMonth() === 0 ? 12 : today.getMonth();
    const lastDay = new Date(year, month, 0).getDate();
    return {
      start_date: `${year}-${String(month).padStart(2, '0')}-01`,
      end_date: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
  }

  return getCurrentMonthRange();
};

const presetOptions = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

const AdminStatsDaily = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dailyRange, setDailyRange] = useState(getCurrentMonthRange);
  const [selectedPreset, setSelectedPreset] = useState('this_month');

  const fetchDailyStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/dashboard/daily-stats', {
        params: {
          start_date: dailyRange.start_date,
          end_date: dailyRange.end_date,
        },
      });
      setDailyStats(response.data.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load daily statistics';
      setDailyStats(null);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [dailyRange.end_date, dailyRange.start_date]);

  useEffect(() => {
    fetchDailyStats();
  }, [fetchDailyStats]);

  const handleDailyRangeChange = (field, value) => {
    if (!value) return;
    const today = getTodayInIsrael();
    setSelectedPreset('custom');
    setDailyRange(prev => {
      const next = { ...prev, [field]: value };
      if (next.start_date > today) next.start_date = today;
      if (next.end_date > today) next.end_date = today;
      if (next.start_date > next.end_date) {
        if (field === 'start_date') next.end_date = next.start_date;
        else next.start_date = next.end_date;
      }
      return next;
    });
  };

  const applyPreset = (preset) => {
    setSelectedPreset(preset);
    setDailyRange(getPresetRange(preset));
  };

  if (currentUser?.role !== 'admin') {
    return <div className="loading">Access denied</div>;
  }

  const summary = dailyStats?.summary;

  return (
    <div className={`admin-page${loading ? ' stats-loading' : ''}`}>
      <div className="page-header">
        <h1>Statistics Daily</h1>
        <div className="header-controls">
          <button className="btn-export btn-export-excel" onClick={() => navigate('/admin/stats')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Statistics
          </button>
        </div>
      </div>

      <div className="stats-filters daily-stats-page-filters">
        <div className="daily-stats-presets">
          {presetOptions.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`daily-stats-preset${selectedPreset === preset.id ? ' active' : ''}`}
              onClick={() => applyPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="filter-group">
          <label>From</label>
          <input
            type="date"
            className="stats-date-input"
            value={dailyRange.start_date}
            max={getTodayInIsrael()}
            onChange={(e) => handleDailyRangeChange('start_date', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>To</label>
          <input
            type="date"
            className="stats-date-input"
            value={dailyRange.end_date}
            max={getTodayInIsrael()}
            onChange={(e) => handleDailyRangeChange('end_date', e.target.value)}
          />
        </div>
      </div>

      <div className="section-title">Summary</div>
      <div className="modal-info daily-stats-page-range">
        <span>{dailyRange.start_date} - {dailyRange.end_date}</span>
        {dailyStats && <span>{dailyStats.summary.days} days</span>}
      </div>

      {error && !loading && (
        <div className="admin-table-wrapper daily-stats-empty-state">
          {error}
        </div>
      )}

      {!error && (
        <>
          <div className="overview-cards daily-stats-overview">
            <div className="overview-card">
              <div className="card-icon blue">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.days ?? 0}</span>
                <span className="card-desc">Days</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon purple">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M3 10h18" />
                  <path d="M8 14h.01" />
                  <path d="M12 14h.01" />
                  <path d="M16 14h.01" />
                  <path d="M8 18h.01" />
                  <path d="M12 18h.01" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.working_days ?? 0}</span>
                <span className="card-desc">Working Days</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon green">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.present ?? 0}</span>
                <span className="card-desc">Present</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon red">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.sick ?? 0}</span>
                <span className="card-desc">Sick</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon yellow">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.vacation ?? 0}</span>
                <span className="card-desc">Vacation</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon purple">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : summary?.missing ?? 0}</span>
                <span className="card-desc">Missing</span>
              </div>
            </div>
            <div className="overview-card">
              <div className="card-icon blue">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="card-data">
                <span className="card-value">{loading ? '--' : `${summary?.total_hours ?? 0}h`}</span>
                <span className="card-desc">Total Hours</span>
              </div>
            </div>
          </div>

          <div className="section-title">Per Day</div>
          <div className="admin-table-wrapper daily-stats-page-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Present</th>
                  <th>Sick</th>
                  <th>Vacation</th>
                  <th>WFH</th>
                  <th>Leave</th>
                  <th>Missing</th>
                  <th>Hours</th>
                  <th>Other</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="9" style={{ textAlign: 'center', color: '#999' }}>Loading...</td></tr>
                )}
                {!loading && dailyStats?.days?.map((day) => (
                  <tr key={day.date} className="daily-stats-row">
                    <td>{new Date(`${day.date}T12:00:00`).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                    <td className="stat-green">{day.present}</td>
                    <td className="stat-red">{day.sick}</td>
                    <td className="stat-blue">{day.vacation}</td>
                    <td>{day.wfh}</td>
                    <td>{day.leave + day.absent}</td>
                    <td>{day.is_workday ? day.missing : '--'}</td>
                    <td>{day.total_hours ? `${day.total_hours}h` : '--'}</td>
                    <td className="daily-other-note">
                      {!day.is_workday ? 'Weekend/Holiday' : day.other > 0 ? day.other : '--'}
                    </td>
                  </tr>
                ))}
                {!loading && !error && dailyStats?.days?.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: 'center', color: '#999' }}>No records</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminStatsDaily;
